import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE } from '../config';
import './ListDashboard.css';

const ListDashboard = () => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [editingList, setEditingList] = useState(null);
  // download job state { [listId]: { job_id, status, download_url } }
  const [jobs, setJobs] = useState({});
  const [editName, setEditName] = useState('');
  // fetch job state { [listId]: { job_id, status, progress } }
  const [fetchJobs, setFetchJobs] = useState({});
  
  const itemsPerPage = 10;

  // Fetch PMIDs for a list
  const pollFetchJobStatus = useCallback((listId, jobId) => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE}/api/fetch_jobs/${jobId}`);
        if (!response.ok) {
          throw new Error('Failed to get job status');
        }
        const data = await response.json();

        setFetchJobs(prev => ({
          ...prev,
          [listId]: {
            ...prev[listId],
            status: data.status,
            process_id: data.process_id,
            started_at: data.started_at
          }
        }));

        if (data.status === 'COMPLETED' || data.status === 'FAILED' || data.status === 'CANCELLED') {
          clearInterval(interval);
          // Instead of calling fetchLists, just update the list to show latest fetch status
          setLists(prev => prev.map(list =>
            list.list_id === listId
              ? {
                  ...list,
                  last_fetch_status: data.status === 'COMPLETED' ? 'COMPLETED' : 'NONE',
                  active_job_id: null
                }
              : list
          ));
        }
      } catch (err) {
        console.error(`Error polling job status for list ${listId}:`, err);
        clearInterval(interval);
        // Optionally update UI to show polling failed
        setFetchJobs(prev => ({
          ...prev,
          [listId]: { ...prev[listId], status: 'FAILED' }
        }));
      }
    }, 3000); // Poll every 3 seconds
  }, []);

  // Fetch all lists
  const fetchLists = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/lists`);
      if (!response.ok) throw new Error('Failed to fetch lists');
      
      const data = await response.json();
      setLists(data);

      // Pre-populate jobs state from the fetched list data
      const initialJobs = data.reduce((acc, list) => {
        if (list.latest_job) {
          acc[list.list_id] = list.latest_job;
        }
        return acc;
      }, {});
      setJobs(initialJobs);
      
      // Pre-populate fetch jobs state from active_job_id or last fetch status
      const initialFetchJobs = data.reduce((acc, list) => {
        if (list.active_job_id) {
          acc[list.list_id] = {
            job_id: list.active_job_id,
            status: list.last_fetch_status || 'RUNNING',
            progress: 0
          };
          // Start polling for active jobs to get real-time updates
          pollFetchJobStatus(list.list_id, list.active_job_id);
        } else if (list.last_fetch_status === 'COMPLETED') {
          // Show 'Fetch Again' for lists with completed fetches
          acc[list.list_id] = {
            status: 'COMPLETED'
          };
        }
        return acc;
      }, {});
      setFetchJobs(initialFetchJobs);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pollFetchJobStatus]);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  // Filter lists based on search term
  const filteredLists = lists.filter(list => 
    list.list_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredLists.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedLists = filteredLists.slice(startIndex, startIndex + itemsPerPage);

  // Create new list
  const handleCreateList = async () => {
    if (!newListName.trim()) return;

    try {
      const response = await fetch(`${API_BASE}/api/lists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ list_name: newListName.trim() })
      });

      if (!response.ok) throw new Error('Failed to create list');
      
      const newList = await response.json();
      setLists(prev => [newList, ...prev]);
      setNewListName('');
      setShowCreateModal(false);
    } catch (err) {
      setError(err.message);
    }
  };

  // Validation method for fetch button
  const canFetchList = (listId) => {
    const fetchJob = fetchJobs[listId];
    // Can fetch if no job exists or if job is completed/failed/cancelled
    return !fetchJob || ['COMPLETED', 'FAILED', 'CANCELLED'].includes(fetchJob.status);
  };

  // Validation method for cancel fetch button
  const canCancelFetch = (listId) => {
    const fetchJob = fetchJobs[listId];
    // Can cancel if job exists and is in pending or running state
    return fetchJob && ['PENDING', 'RUNNING'].includes(fetchJob.status);
  };

  // Get fetch button text based on job status
  const getFetchButtonText = (listId) => {
    const fetchJob = fetchJobs[listId];
    if (!fetchJob) return 'Fetch';
    if (fetchJob.status === 'COMPLETED') return 'Fetch Again';
    if (fetchJob.status === 'FAILED') return 'Retry Fetch';
    if (fetchJob.status === 'CANCELLED') return 'Fetch Again';
    return `Fetching...`;
  };

  const handleFetchList = async (listId) => {
    try {
      // Set initial job state to show loading
      setFetchJobs(prev => ({
        ...prev,
        [listId]: {
          status: 'RUNNING',
          progress: 0
        }
      }));
      
      const response = await fetch(`${API_BASE}/api/lists/${listId}/start_background_fetch`, {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start fetch');
      }

      const data = await response.json();

      // Update the fetch job state with the new job ID and status
      setFetchJobs(prev => ({
        ...prev,
        [listId]: { 
          job_id: data.job_id, 
          status: 'RUNNING' 
        }
      }));

      // Start polling for the job status
      pollFetchJobStatus(listId, data.job_id);

    } catch (err) {
      console.error('Error starting fetch:', err);
      alert(`Error starting fetch: ${err.message}`);
      setFetchJobs(prev => ({
        ...prev,
        [listId]: { ...prev[listId], status: 'FAILED' }
      }));
      setError(err.message);
    }
  };

  // Cancel a fetch job
  const cancelFetch = async (listId) => {
    try {
      const response = await fetch(`${API_BASE}/api/lists/${listId}/cancel_fetch`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel fetch');
      }
      
      // Update the fetch job state to CANCELLED instead of removing it
      setFetchJobs(prev => ({
        ...prev,
        [listId]: {
          ...prev[listId],
          status: 'CANCELLED'
        }
      }));
      
      alert('Fetch cancelled successfully');
    } catch (err) {
      console.error('Error cancelling fetch:', err);
      alert(`Error cancelling fetch: ${err.message}`);
    }
  };

  // Export list as CSV
  const handleExportList = async (listId) => {
    try {
      const response = await fetch(`${API_BASE}/api/lists/${listId}/export_list_csv_async`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error('Failed to start export job');
      
      const data = await response.json();
      // Set initial job state
      setJobs(prev => ({
        ...prev,
        [listId]: {
          job_id: data.job_id,
          status: 'PENDING'
        }
      }));
      
      // Start polling for job status
      pollExportJobStatus(listId, data.job_id);
    } catch (err) {
      setError(err.message);
    }
  };

  // Poll for export job status
  const pollExportJobStatus = async (listId, jobId) => {
    const poll = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/download_jobs/${jobId}`);
        if (!response.ok) {
          // Job not found, stop polling
          setJobs(prev => {
            const newState = { ...prev };
            delete newState[listId];
            return newState;
          });
          return;
        }
        
        const data = await response.json();
        
        setJobs(prev => ({
          ...prev,
          [listId]: {
            job_id: jobId,
            status: data.status || 'PENDING',
            retrieved_records: data.retrieved_records,
            download_url: data.download_url
          }
        }));
        
        // Continue polling if job is not completed
        if (data.status !== 'COMPLETED' && data.status !== 'FAILED') {
          setTimeout(poll, 2000); // Poll every 2 seconds
        }
      } catch (err) {
        console.error('Error polling export job status:', err);
        // Stop polling on error
        setJobs(prev => {
          const newState = { ...prev };
          delete newState[listId];
          return newState;
        });
      }
    };
    
    // Start polling
    poll();
  };

  // Download CSV file
  const handleDownloadCSV = (downloadUrl) => {
    window.open(`${API_BASE}${downloadUrl}`, '_blank');
  };

  // Delete a list
  const handleDeleteList = async (listId) => {
    if (!window.confirm('Are you sure you want to delete this list? This action cannot be undone.')) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/lists/${listId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) throw new Error('Failed to delete list');
      
      setLists(prev => prev.filter(list => list.list_id !== listId));
      // Also remove any job state for this list
      setJobs(prev => {
        const newState = { ...prev };
        delete newState[listId];
        return newState;
      });
      setFetchJobs(prev => {
        const newState = { ...prev };
        delete newState[listId];
        return newState;
      });
    } catch (err) {
      setError(err.message);
    }
  };

  // Update a list name
  const handleUpdateList = async (listId) => {
    if (!editName.trim()) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/lists/${listId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ list_name: editName.trim() })
      });

      if (!response.ok) throw new Error('Failed to update list');
      
      setLists(prev => 
        prev.map(list => 
          list.list_id === listId 
            ? { ...list, list_name: editName.trim() } 
            : list
        )
      );
      setEditingList(null);
      setEditName('');
    } catch (err) {
      setError(err.message);
    }
  };

  // CSV export features removed as per requirements

  // CSV export features removed as per requirements

  if (loading) return <div className="loading">Loading lists...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (filteredLists.length === 0) return (
    <div className="list-dashboard">
      <div className="dashboard-header">
        <h1>Manage PubMed Lists</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          Create New List
        </button>
      </div>
      <div className="search-section">
        <input
          type="text"
          placeholder="Search lists..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>
      <div className="no-data">
        {searchTerm ? `No lists found matching "${searchTerm}"` : "You don't have any lists yet. Create your first list to get started!"}
      </div>
    </div>
  );

  return (
    <div className="list-dashboard">
      {/* Date modal removed as CSV features are removed */}

      <div className="dashboard-header">
        <h1>Manage PubMed Lists</h1>
        <button 
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          Create New List
        </button>
      </div>

      <div className="search-section">
        <input
          type="text"
          placeholder="Search lists..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="lists-table-container">
        <table className="lists-table">
          <thead>
            <tr>
              <th>List Name</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Queries</th>
              <th>Retrieved</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedLists.map(list => (
              <tr key={list.list_id}>
                <td>
                  {editingList === list.list_id ? (
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="edit-input"
                      autoFocus
                    />
                  ) : (
                    <span>{list.list_name}</span>
                  )}
                </td>
                <td>{new Date(list.created_at).toLocaleDateString()}</td>
                <td>{new Date(list.updated_at).toLocaleDateString()}</td>
                <td>{list.query_count || 0}</td>
                <td>{list.latest_job?.retrieved_records || 0}</td>
                <td className="actions">
                  {editingList === list.list_id ? (
                    <>
                      <button 
                        className="btn btn-sm btn-success"
                        onClick={() => handleUpdateList(list.list_id)}
                      >
                        Save
                      </button>
                      <button 
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          setEditingList(null);
                          setEditName('');
                        }}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button 
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          setEditingList(list.list_id);
                          setEditName(list.list_name);
                        }}
                      >
                        Edit
                      </button>
                      <Link
                        to={`/lists/${list.list_id}`}
                        className="btn btn-sm btn-info"
                      >
                        Manage
                      </Link>
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => handleFetchList(list.list_id)}
                        disabled={!canFetchList(list.list_id)}
                      >
                        {getFetchButtonText(list.list_id)}
                      </button>
                      {canCancelFetch(list.list_id) && (
                        <>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => cancelFetch(list.list_id)}
                          >
                            Stop Fetching
                          </button>
                          <div className="fetch-progress-info">
                            {typeof fetchJobs[list.list_id].current_query !== 'undefined' && fetchJobs[list.list_id].current_query !== '' && (
                              <span>Query: {fetchJobs[list.list_id].current_query}</span>
                            )}
                            {typeof fetchJobs[list.list_id].completed_queries !== 'undefined' && typeof fetchJobs[list.list_id].total_queries !== 'undefined' && (
                              <span>Queries: {fetchJobs[list.list_id].completed_queries}/{fetchJobs[list.list_id].total_queries}</span>
                            )}
                            {typeof fetchJobs[list.list_id].pmids_added !== 'undefined' && typeof fetchJobs[list.list_id].total_pmids !== 'undefined' && (
                              <span>PMIDs: {parseInt(fetchJobs[list.list_id].pmids_added).toLocaleString()}/{parseInt(fetchJobs[list.list_id].total_pmids).toLocaleString()}</span>
                            )}
                          </div>
                        </>
                      )}
                      <button 
                        className="btn btn-sm btn-success"
                        onClick={() => handleExportList(list.list_id)}
                        disabled={!!jobs[list.list_id]}
                      >
                        {jobs[list.list_id] ? (jobs[list.list_id].status === 'COMPLETED' ? 'Download CSV' : `Exporting... ${jobs[list.list_id].status}`) : 'Generate CSV'}
                      </button>
                      {jobs[list.list_id] && jobs[list.list_id].status === 'COMPLETED' && jobs[list.list_id].download_url && (
                        <button 
                          className="btn btn-sm btn-info"
                          onClick={() => handleDownloadCSV(jobs[list.list_id].download_url)}
                        >
                          Download
                        </button>
                      )}
                      <button 
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteList(list.list_id)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="btn btn-secondary"
          >
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="btn btn-secondary"
          >
            Next
          </button>
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Create New List</h3>
            <input
              type="text"
              placeholder="Enter list name"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              className="modal-input"
              autoFocus
            />
            <div className="modal-actions">
              <button 
                className="btn btn-primary"
                onClick={handleCreateList}
              >
                Create
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowCreateModal(false);
                  setNewListName('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListDashboard;